'use client';

import React from 'react';
import Link from 'next/link';

const HeroSection: React.FC = () => {
  return (
    <section className="relative w-full" style={{ minHeight: '100vh' }}>
      {/* Full-width background video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
      >
        <source src="/assets/videos/hero-1-z2zzv4.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Two-panel content overlay */}
      <div className="relative z-10 flex items-center py-24 lg:py-0 lg:h-screen lg:min-h-0" style={{ minHeight: 'inherit' }}>
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-10">

          {/* Left Panel — First time shipping? */}
          <div className="flex-1 text-white max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-300 mb-3">First time shipping?</p>
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-5">
              Ship with<br />confidence.
            </h1>
            <p className="text-xl text-gray-200 mb-9">
              Sea, air, inland freight &amp; customs — 16+ countries, 25+ years of expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/transport-quote"
                className="inline-flex items-center justify-center px-8 py-4 text-white font-semibold rounded-lg transition-colors duration-200"
                style={{ backgroundColor: '#3CB371' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2E9959')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#3CB371')}
              >
                Get a Free Quote
              </Link>
              <Link
                href="/contacts"
                className="inline-flex items-center justify-center px-8 py-4 font-semibold rounded-lg transition-all duration-200"
                style={{ border: '2px solid #6FC4EA', color: '#6FC4EA', backgroundColor: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6FC4EA'; e.currentTarget.style.color = '#ffffff'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6FC4EA'; }}
              >
                Talk to an Expert
              </Link>
            </div>
            <p className="mt-5 text-sm text-gray-400">Response within 2 hours — Mon–Fri</p>
          </div>

          {/* Right Panel — MY WTO Portal */}
          <div className="flex-shrink-0 w-full lg:w-96 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-white">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-300 mb-1">MY WTO Portal</p>
            <h2 className="text-3xl font-bold mb-2">Welcome<br />back.</h2>
            <p className="text-sm text-gray-300 mb-6">
              Track shipments, manage bookings, and access your documents — all in one place.
            </p>

            <div className="flex flex-col gap-2">
              <a
                href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 group text-white"
                style={{ backgroundColor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', color: '#1B6B8A' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f9ff'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
              >
                <div>
                  <p className="font-semibold text-sm" style={{ color: '#1B6B8A' }}>Track Shipment</p>
                  <p className="text-xs" style={{ color: '#1B6B8A', opacity: 0.8 }}>Live status &amp; location</p>
                </div>
                <svg className="w-4 h-4" style={{ color: '#1B6B8A' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <a
                href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 group text-white"
                style={{ backgroundColor: '#3CB371', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2E9959')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#3CB371')}
              >
                <div>
                  <p className="font-semibold text-sm text-white">Book a Shipment</p>
                  <p className="text-xs text-white/80">Schedule your next delivery</p>
                </div>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <a
                href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 group text-white"
                style={{ backgroundColor: '#0DA9C8', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0890AB')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#0DA9C8')}
              >
                <div>
                  <p className="font-semibold text-sm text-white">Statements</p>
                  <p className="text-xs text-white/80">Invoices &amp; account documents</p>
                </div>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <a
                href="/portal-registration"
                className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 group text-white"
                style={{ backgroundColor: '#1B6B8A', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#155570')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#1B6B8A')}
              >
                <div>
                  <p className="font-semibold text-sm text-white">Portal Registration</p>
                  <p className="text-xs text-white/80">Request your portal access</p>
                </div>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            <p className="mt-4 text-xs text-gray-400 text-center">
              Portal access requires registration. Use Portal Registration above to get started.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;